import { Card } from "react-bootstrap";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import ResolveModal from "../../components/ResolveModal/ResolveModal";
import { EyeFill } from "react-bootstrap-icons";
import ViewModal from "../../components/viewModal/viewModal";
import HelperService from "../../utility/HelperService";

const ContactUsList = () => {
  const [records, setRecords] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"PENDING" | "RESOLVED">("PENDING");

  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAt");

  const [resolveData, setResolveData] = useState<any>(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState<any>(null);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const sortFieldMap: Record<string, string> = {
    name: "name",
    email: "email",
    createdAt: "createdAt",
    category: "category",
  };

  const fetchData = (keyword = "") => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());
    params.append("status", activeTab);
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);

    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    WebService.getAPI({
      action: `contact-us/list?${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};
        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        const list = res?.data?.data || [];

        const rows = list.map((item: any) => ({
          ...item,
          createdAt: HelperService.dateFormatter(item.createdAt),
          category: item?.category?.name || "-",

          // SHOW ONLY IN RESOLVED TAB
          actionBy:
            item.status === "RESOLVED" ? item?.actionBy?.name || "-" : "-",

          comment: item.status === "RESOLVED" ? item?.comment || "-" : "-",

          // SHOW ONLY IN PENDING TAB
          action: (
            <div className="d-flex gap-2 justify-content-center">
              {/* VIEW ALWAYS VISIBLE */}
              {/* <button
        className="btn btn-sm btn-info"
        onClick={() => handleView(item)}
      >
        View
      </button> */}
              {perm.canEdit && (
                <EyeFill
                  className="me-3 cursor-pointer"
                  size={18}
                  onClick={() => {
                    handleView(item);
                  }}
                  title="View"
                />
              )}
              {/* RESOLVE ONLY FOR PENDING */}
              {perm.canEdit && (
                <>
                  {item.status === "PENDING" && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleResolve(item)}
                    >
                      Resolve
                    </button>
                  )}{" "}
                </>
              )}
            </div>
          ),
        }));

        setRecords(rows);
        setTotalCount(res?.data?.pagination?.total || 0);
      })
      .catch(() => {});
  };

  // 🔄 Fetch table data on state change
  useEffect(() => {
    const delay = setTimeout(() => {
      fetchData(search);
    }, 300);

    return () => clearTimeout(delay);
  }, [currentPage, search, activeTab, sortField, sortOrder]);

  // ⭐ Columns for table
  const baseColumns = [
    {
      id: "createdAt",
      label: "Created",
      sortable: true,
      minWidth: 130,
      align: "center" as const,
    },
    {
      id: "name",
      label: "Name",
      sortable: true,
      minWidth: 150,
    },
    {
      id: "email",
      label: "Email",
      sortable: true,
      minWidth: 150,
    },
    {
      id: "category",
      label: "Category",
      sortable: true,
      minWidth: 150,
    },
  ];
  const handleView = (item: any) => {
    setViewData(item); // ✅ data set
    setShowViewModal(true); // ✅ only view modal open
    setShowResolveModal(false);
  };
  const closeViewModal = () => {
    setShowViewModal(false);
  };

  const columns =
    activeTab === "RESOLVED"
      ? [
          ...baseColumns,
          {
            id: "actionBy",
            label: "Action By",
            sortable: false,
            minWidth: 150,
          },
          {
            id: "comment",
            label: "Comment",
            sortable: false,
            minWidth: 200,
          },
          {
            id: "action",
            label: "Action",
            minWidth: 100,
            align: "center" as const,
          },
        ]
      : [
          ...baseColumns,
          {
            id: "action",
            label: "Action",
            minWidth: 100,
            align: "center" as const,
          },
        ];

  // Open Resolve Modal
  const handleResolve = (item: any) => {
    setResolveData(item); // ✅ data set
    setShowResolveModal(true); // ✅ only resolve modal open
    setShowViewModal(false);
  };

  return (
    <>
      <ResolveModal
        show={showResolveModal}
        data={resolveData}
        handleClose={() => {
          setShowResolveModal(false);
          setResolveData(null);
          fetchData();
        }}
      />
      <ViewModal
        show={showViewModal}
        data={viewData}
        onClose={() => {
          setShowViewModal(false);
          setViewData(null);
        }}
      />

      <div className="events-main">
        <Card className="event-card p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="page-title">Contact Requests</div>
          </div>

          {/* SEARCH */}
          <div className="d-flex mb-3">
            <input
              type="text"
              placeholder="Search name / email / message"
              className="form-control"
              style={{ width: "280px" }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* TABS */}
          <div className="d-flex gap-4 mb-3">
            <div
              style={{
                cursor: "pointer",
                paddingBottom: "6px",
                borderBottom:
                  activeTab === "PENDING"
                    ? "2px solid #007bff"
                    : "2px solid transparent",
                color: activeTab === "PENDING" ? "#007bff" : "#555",
                fontWeight: activeTab === "PENDING" ? "600" : "400",
              }}
              onClick={() => {
                setActiveTab("PENDING");
                setCurrentPage(1);
              }}
            >
              Pending
            </div>

            <div
              style={{
                cursor: "pointer",
                paddingBottom: "6px",
                borderBottom:
                  activeTab === "RESOLVED"
                    ? "2px solid #007bff"
                    : "2px solid transparent",
                color: activeTab === "RESOLVED" ? "#007bff" : "#555",
                fontWeight: activeTab === "RESOLVED" ? "600" : "400",
              }}
              onClick={() => {
                setActiveTab("RESOLVED");
                setCurrentPage(1);
              }}
            >
              Resolved
            </div>
          </div>

          {/* SORTABLE TABLE */}
          <CustomTableSortable
            columns={columns}
            rows={records}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            sortField={uiSortField}
            sortOrder={sortOrder}
            isLoading={false}
            onPageChange={(page) => setCurrentPage(page)}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);

              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);

              setCurrentPage(1);
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default ContactUsList;
